import type {
  DecisionStatusEnum,
  ExecutionReport,
  IOMSNewOrderSingle4200Aggregations,
  OMSOrderPropertyEnum,
  PricingMode,
  SideEnum,
} from '@talos/kyoko';
import type { FullOrderRequest } from '../../../providers/orders.types';

/** Bin data with ISO date strings */
export interface PreTradeBin {
  StartTime: string;
  EndTime: string;
  Volume: string;
  StartTargetQty: string;
  EndTargetQty: string;
  StartUpperBound: string;
  EndUpperBound: string;
  StartLowerBound: string;
  EndLowerBound: string;
  BinQty: string;
}

export interface PreTradeAnalytic {
  Property: OMSOrderPropertyEnum;
  Value: string;
  /** Optional market name when analytic is market-specific */
  Market?: string;
  /** Optional symbol when multiple symbols exist per market (unified liquidity) */
  Symbol?: string;
}

/** Fields from the NOS that were considered in the pre-trade analysis */
export interface PreTradeNOSConsideredField {
  Name: string;
}

/**
 * Market item inside `NewOrderSingle.Markets`.
 * This is the richer request/order payload shape used for resubmit priming.
 */
export interface PreTradeNOSMarket {
  Market?: string;
  MarketAccount?: string;
  LegIndex?: number;
  Symbol?: string;
  ParentSymbol?: string;
}

/**
 * Request-shaped NewOrderSingle payload used for pre-trade submit/compare flows.
 */
export type PreTradeRequestNewOrderSingle = Omit<FullOrderRequest, 'Markets'> & {
  Side: SideEnum | string;
  PricingMode?: PricingMode;
  InitialDecisionStatus?: DecisionStatusEnum;
  StartTime?: string;
  EndTime?: string;
  Aggregations?: IOMSNewOrderSingle4200Aggregations[];
  Markets: PreTradeNOSMarket[];
};

/**
 * Scenario/inbound NewOrderSingle from pre-trade responses.
 * It is request-shaped and may optionally include a small subset of execution-style fields.
 */
export type PreTradeScenarioNewOrderSingle = PreTradeRequestNewOrderSingle &
  Partial<Pick<ExecutionReport, 'DecisionStatus' | 'OrdStatus' | 'CumQty' | 'LegSummary' | 'MultilegParams'>>;

/** Pre-trade request containing SessionID and Order */
export interface PreTradeRequest {
  SessionID: string;
  /**
   * New-order request payload produced by OrderService.generateRequest.
   * This is request-shaped (markets as string[]), not a hydrated Order entity.
   * Granular unified-liquidity orders use MarketSymbols instead of Markets.
   */
  Order: Omit<PreTradeRequestNewOrderSingle, 'Markets'> & Pick<FullOrderRequest, 'Markets' | 'MarketSymbols'>;
}

/** API response data */
export interface PreTradeResponseData {
  /** Organization ID used mostly internally to identify the organization in multi-tenant environments. */
  OrgID?: number;
  /** Internal time in number of nanoseconds since Unix epoch when the response was generated. */
  Timestamp: string;
  /** User ID of the user who requested the pre-trade trajectory. */
  User: string;
  /** Internal time in number of nanoseconds since Unix epoch when request was submitted. */
  RequestedTimestamp: string;
  /** Request ID of the request (internal). */
  RequestID: string;
  /** Parent security of the order */
  Symbol: string;
  /** Strategy ID of the order strategy used */
  Strategy: string;
  /** Overall start time of the full trajectory either computed as now or provided by the user. */
  StartTime: string;
  /** Overall end time of the full trajectory either computed or provided by the user. */
  EndTime: string;
  /** Overall quantity to be scheduled denominated in the parent order currency. */
  Quantity: string;
  /** Status either Success or Error. */
  Status: string;
  /** Bounds control index used by bounded algos */
  BoundsControl?: number;
  /** Algo type that was requested e.g a VWAP. */
  RequestedAlgoType: string;
  /** Algo type that was returned e.g. a VWAP can fallback to a TWAP. */
  ResponseAlgoType: string;
  /** Parent order currency of the order. */
  Currency?: string;
  /** Arrival-aware snipe bound (price improvement). */
  SnipeBound?: string;
  /** Arrival-aware sweep bound (price worsening). */
  SweepBound?: string;
  /** Trajectory bins denominated in 1 minute resolution. */
  Bins: PreTradeBin[];
  /** Analytics per order and also per market are bundled in the same analytics. */
  Analytics: PreTradeAnalytic[];
  /** Fields used in the calculations, all others can be assumed to be unused. */
  NOSConsideredFields: PreTradeNOSConsideredField[];
  /** Scenario-level market summary; not the same as `NewOrderSingle.Markets`. */
  Markets: Array<{ Market?: string; Symbol?: string }>;
  /** The original NewOrderSingle request payload (untouched) sent to pre-trade service. */
  NewOrderSingle: PreTradeScenarioNewOrderSingle;
  /** Optional passthrough identifier for the pre-trade session. */
  SessionIdentifier?: string;
  /** Optional text message returned by the pre-trade service used mostly for error messages. */
  Text?: string;
}

export interface PreTradeResponse {
  type: 'PreTradeResponse';
  ts: string;
  data: PreTradeResponseData[];
}

// Compatibility aliases used by existing imports/tests.
export type PreTradeNewOrderSingle = PreTradeRequestNewOrderSingle;
export type PreTradeMarket = PreTradeNOSMarket;
export type PreTradeOrderRequest = FullOrderRequest;

/** Query params for pre-trade sessions/data endpoint. */
export interface PreTradeDataParams {
  [key: string]: string | undefined;
  Symbol?: string;
  Strategy?: string;
  StartDate?: string;
  EndDate?: string;
  SessionID?: string;
  RequestID?: string;
  Limit?: string;
}

// Analytics property constants
export const PreTradeAnalyticProperty = {
  SpreadCost: 'SpreadCost',
  TemporaryCost: 'TemporaryCost',
  TimeRisk: 'TimeRisk',
} as const;

export type PreTradeAnalyticProperty = (typeof PreTradeAnalyticProperty)[keyof typeof PreTradeAnalyticProperty];
